import './query-tkgm.scss';
import WKT from 'ol/format/WKT';
import { Button } from 'primereact/button';
import { Dropdown } from 'primereact/dropdown';
import { InputText } from 'primereact/inputtext';
import React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { setFitGeom, setHighlightGeom } from '../../../features/mapSlice';
import { setShowLoading } from '../../../features/sharedSlice';
import { useAppSelector } from '../../../store/hooks';
import { ExternalServicesRestApi } from '../../../utils/restapi/external-services';
import { DrawTypes, setTextStatus, startDrawing } from '../../../features/drawSlice';
import { Feature } from 'ol';
import { Polygon } from 'ol/geom';
import { Toast } from 'primereact/toast';
import { setSelectedFeature } from '../../../features/selectionSlice';

export default function QueryTKGM() {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const showTKGM = useAppSelector((state) => state.toolbarState.showTKGM);
  const drawFeature = useAppSelector((state) => state.drawState.drawFeature);
  const token = useAppSelector((state) => state.authState.token);

  const [drawState, setDrawState] = useState<boolean>(false);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [tkgmFields, setTkgmFields] = useState<any[]>([
    {
      id: 1,
      alias: 'İl',
      type: 'Select',
    },
    {
      id: 2,
      alias: 'İlçe',
      type: 'Select',
    },
    {
      id: 3,
      alias: 'Mahalle',
      type: 'Select',
    },
    {
      id: 4,
      alias: 'Ada',
      type: 'Text',
    },
    {
      id: 5,
      alias: 'Parsel',
      type: 'Text',
    },
  ]);

  const toast = useRef<any>(null);

  useEffect(() => {
    // @ts-ignore
    if (showTKGM && drawFeature && drawFeature.getGeometry && drawState) {
      checkArea();
    }
  }, [drawFeature]);

  useEffect(() => {
    if (showTKGM) {
      getIl();
    } else {
      setDrawState(false);
      setFeatures([]);
      dispatch(setTextStatus(true));
      dispatch(startDrawing(DrawTypes.CLEAR));
      dispatch(setFitGeom(null));
      dispatch(setHighlightGeom(null));
    }
  }, [showTKGM]);

  const getIl = async () => {
    const fields = [...tkgmFields];
    dispatch(setShowLoading(true));
    await ExternalServicesRestApi.getTKGMIl(token).then((res: any) => {
      if (res && res.data) {
        const responseObject = res.data;
        fields[0].domain = responseObject.result;
        setTkgmFields(fields);
      }
    }
    ).catch((err) => {
      if (err) {
        dispatch(setShowLoading(false));
        // aşağıdaki kısım eğer pecere kapatılsın istenirse açılabilinir
        // dispatch(setShowTKGM(false));
        toast.current.show({
          severity: 'error',
          summary: t('TEXT.Fail'),
          detail: err.response.data.message ? err.response.data.message : t(`TEXT.Unknown Error`),
          duration: 3000
        });
      }
    });
    dispatch(setShowLoading(false));
  };

  const getIlce = async (ilID: number, fields: any[]) => {
    const theField = fields[0].domain.filter((iter: any) => {
      return iter.value === ilID
    })
    const name = theField[0].name ? theField[0].name : ""
    dispatch(setShowLoading(true));
    const res = await ExternalServicesRestApi.getTKGMIlce(ilID, token, name);
    dispatch(setShowLoading(false));

    if (res && res.data) {
      const responseObject = res.data;
      fields[1].domain = responseObject.result;

      setTkgmFields(fields);
    } else {
      toast.current.show({
        severity: 'error',
        summary: t('TEXT.Fail'),
        detail: t(`TEXT.Unknown Error`),
      });
    }
  };

  const getMahalle = async (ilceID: number, fields: any[]) => {
    const theField = fields[1].domain.filter((iter: any) => {
      return iter.value === ilceID
    })
    const name = theField[0].name ? theField[0].name : ""
    dispatch(setShowLoading(true));
    const res = await ExternalServicesRestApi.getTKGMMahalle(ilceID, token, name);
    dispatch(setShowLoading(false));

    if (res && res.data) {
      const responseObject = res.data;
      fields[2].domain = responseObject.result;

      setTkgmFields(fields);
    } else {
      toast.current.show({
        severity: 'error',
        summary: t('TEXT.Fail'),
        detail: t(`TEXT.Unknown Error`),
      });
    }
  };

  const getParselInfo = async () => {
    const fields = tkgmFields;

    const body = {
      mahalleID: fields[2].value,
      adaNo: fields[3].value,
      parselNo: fields[4].value,
    };

    dispatch(setShowLoading(true));
    const res = await ExternalServicesRestApi.getParsel(body, token);
    dispatch(setShowLoading(false));

    if (res && res.data) {
      if (res.data.success) {
        const geomString = res.data.geomString;

        const wktFormat = new WKT();

        const feature :any = wktFormat.readFeature(geomString);

        const geometry = feature.getGeometry();

        dispatch(
          setFitGeom({
            type: geometry!.getType(),
            // @ts-ignore
            coordinates: geometry!.getCoordinates(),
          })
        );
        dispatch(
          setHighlightGeom({
            type: geometry!.getType(),
            // @ts-ignore
            coordinates: geometry!.getCoordinates(),
          })
        );
      } else {
        toast.current.show({
          severity: 'error',
          summary: t('TEXT.Fail'),
          detail: t(`TEXT.${res.data.message}`),
        });
      }
    } else {
      toast.current.show({
        severity: 'error',
        summary: t('TEXT.Fail'),
        detail: t(`TEXT.Unknown Error`),
      });
    }
  };

  const drawWithArea = async () => {
    setDrawState(true);
    dispatch(setTextStatus(false));
    dispatch(startDrawing(DrawTypes.POLYGON));
  };

  const checkArea = async () => {
    // @ts-ignore
    const feature :any = drawFeature as Feature;

    const polygonGeom = feature.getGeometry() as Polygon;
    const geomArea = polygonGeom.getArea();
    if (geomArea > 100000) {
      toast.current.show({
        severity: 'error',
        summary: t('TEXT.Fail'),
        detail: '100000 metrekare alandan fazla sorgulayamazsınız',
      });
      setDrawState(false);
      dispatch(startDrawing(DrawTypes.CLEAR));
    } else {
      const format = new WKT();

      const wkt = format.writeFeature(feature, {
        dataProjection: 'EPSG:4326',
        featureProjection: 'EPSG:3857',
      });

      const body = {
        geomString: wkt,
      };

      dispatch(setShowLoading(true));
      const res = await ExternalServicesRestApi.getParselArea(body, token);
      if (!res) {
        dispatch(setShowLoading(false));
        toast.current.show({
          severity: 'error',
          summary: t('TEXT.Fail'),
          detail: 'TKGM Servisine Ulaşılamıyor.',
        });
      } else {
        dispatch(setShowLoading(false));
      }


      if (res && res.data) {
        const parselArray: any[] = res.data.result;

        if (parselArray.length > 0) {
          const featureArray = parselArray.map((parselObject) => {
            const feature :any = format.readFeature(parselObject.geom);

            delete parselObject.geom;
            feature.setProperties(parselObject);
            feature.set('tkgm', true);

            return feature;
          });

          setFeatures(featureArray);
        } else {
          toast.current.show({
            severity: 'error',
            summary: t('TEXT.Fail'),
            detail: 'Parsel Bulunamadı',
          });
        }
      } else {
        toast.current.show({
          severity: 'error',
          summary: t('TEXT.Fail'),
          detail: t(`TEXT.Unknown Error`),
        });
      }
    }
  };

  const onInputChanged = (value: any, field: any) => {
    const copyFields = [...tkgmFields];

    const fieldIndex = copyFields.findIndex((copyField) => copyField.id === field.id);
    copyFields[fieldIndex] = { ...copyFields[fieldIndex], value: value };

    if (field.id === 1) {
      copyFields[fieldIndex + 1] = {
        ...copyFields[fieldIndex + 1],
        value: undefined,
        domain: undefined,
      };
      copyFields[fieldIndex + 2] = {
        ...copyFields[fieldIndex + 2],
        value: undefined,
        domain: undefined,
      };

      getIlce(value, copyFields);
    } else if (field.id === 2) {
      copyFields[fieldIndex + 1] = {
        ...copyFields[fieldIndex + 1],
        value: undefined,
        domain: undefined,
      };

      getMahalle(value, copyFields);
    } else {
      setTkgmFields(copyFields);
    }
  };

  const closeResults = () => {
    setFeatures([])
    setDrawState(false);
    dispatch(startDrawing(DrawTypes.CLEAR));
  }

  return (
    <>
      {showTKGM && (
        <>
          <div className="tkgm-container">
            <div className="container">
              <h2 className="tkgm-title">TKGM Sorgulama</h2>
              {tkgmFields.map((field) => (
                <React.Fragment key={field.alias}>
                  {field.type === 'Select' && (
                    <div className="field">
                      <label>{field.alias}</label>
                      <Dropdown
                        value={field.value}
                        options={field.domain}
                        disabled={!field.domain}
                        filter showClear filterBy="name"
                        optionLabel="name"
                        optionValue="value"
                        placeholder={t('Select')}
                        onChange={(e) => onInputChanged(e.target.value, field)}
                      />
                    </div>
                  )}
                  {field.type === 'Text' && (
                    <div className="field">
                      <label>{field.alias}</label>
                      <InputText onChange={(e) => onInputChanged(e.target.value, field)} />
                    </div>
                  )}
                </React.Fragment>
              ))}
              <Button className="query-button up" disabled={true ? (drawState || !tkgmFields[2].value || !tkgmFields[3].value || !tkgmFields[4].value) : false} onClick={() => getParselInfo()}>
                Sorgula
              </Button>
              <Button className="query-button" onClick={() => drawWithArea()}>
                Alan Çizerek Getir
              </Button>
            </div>
            {features.length > 0 && (
              <>
                <Button className='close' icon="pi pi-times" onClick={() => { closeResults() }} />
                <div className="feature-container">
                  <div className="container">
                    <h3 style={{ margin: 0 }}>TKGM Alan Sorgu Sonucu</h3>
                    {features.map((feature) => (
                      <React.Fragment key={feature.get('tapu_zemin_ref')}>
                        <div className="feature">
                          <label className="mini-label">Ada: {feature.get('ada')}</label>
                          <label className="mini-label">Parsel: {feature.get('parsel')}</label>
                          <Button className="zoom-button" onClick={() => dispatch(setSelectedFeature(feature))}>
                            Bilgi Al
                          </Button>
                        </div>
                        <hr className="split"></hr>
                      </React.Fragment>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </>
      )}
      <Toast ref={toast} position="bottom-center"></Toast>
    </>
  );
}
